// Transport limits for the imports API — PE-1991 (raised PE-1998).
//
// THE CEILING ON `imports.validate` IS REQUEST-BODY BYTES, NOT ROW COUNT.
//
// `validate` sends every data row in the POST body. The shared `ApiRouter`
// (infra/api-router.ts) fronts the Lambda URL with CloudFront + a Lambda@Edge
// signer, and Lambda@Edge caps a request body at 1MB. Originally bracketed on
// deployed dev during PE-1991 (run-d3-validate-ceiling.mjs) between two
// row-shaped datapoints:
//
//   5,000 rows x 4 cols = 0.38 MB -> 200, 1.8s
//  12,000 rows x 2 cols = 0.49 MB -> 200
//  15,000 rows x 4 cols = 1.17 MB -> 413 from the EDGE
//
// 12k rows passing while 15k fails is the proof that the unit is bytes: a
// narrow file survives a row count that a wide one does not. Any bound
// expressed in rows is therefore wrong for some real file — a 4-column export
// and a 20-column export hit the wall at completely different row counts.
//
// PE-1998 CLOSED THE BRACKET. `tests/evals/qa-imports/probe-edge-body-ceiling.mjs`
// binary-searches a padded, byte-guard-free route (`jobs.list`) against the
// same deployed edge, so the ONLY thing that can refuse a large body is
// CloudFront itself. Run three times, converged to the SAME byte, each time:
//
//   1,024 KB (1,048,576 B) -> reaches the origin (tRPC 405, "unsupported
//                              POST to query procedure" — expected for a
//                              query proc called as a mutation; the point is
//                              it is OUR envelope, not CloudFront's)
//   1,025 KB (1,049,600 B) -> {"error":"Request body exceeds 1MB Lambda@Edge
//                              limit. Use presigned S3..."} — CloudFront's own
//                              message, not ours. No tRPC envelope, no
//                              Powertools log, no CloudWatch entry.
//
// The wall is therefore an EXACT 1 MiB (1,048,576 bytes), not a mushy zone —
// CloudFront/Lambda@Edge names its own limit in the refusal body. This is
// materially better than the PE-1991 bracket, which only knew the wall sat
// somewhere in a ~700KB-wide band.
//
// WHY THE EDGE 413 IS STILL THE FAILURE WE ARE BUYING OUT OF, even though the
// wall is now precisely known. It is generated before the origin, so it
// produces no Powertools log, no CloudWatch entry, no metric filter and no
// alarm, and its body is neither our Hono envelope nor a tRPC envelope.
// Commitment 10 requires every error path to reach CloudWatch; this one
// cannot, because our code never runs. The only fix available to us is to
// refuse BEFORE the request is sent (client pre-flight) or at the first line
// of our own code that does run (the Zod bound below) — never to "handle"
// the 413.
//
// WHY THE BUDGET IS STILL WELL UNDER 1,048,576. Even with the wall known
// exactly, `measureValidateBodyBytes` does not count everything that travels:
// the tRPC envelope, the JSON-RPC wrapper, and the request headers all add
// bytes on top of `{headers, rows}`. The budget below is 80% of the measured
// last-passed size (1,024 KB), which reserves ~205KB of headroom for that
// difference. If CloudFront's limit ever moves (it is AWS's to change,
// undocumented as a contract), re-run the probe and re-derive this constant —
// do not hand-tune it upward without a fresh measurement.
export const VALIDATE_MAX_BODY_BYTES = 810 * 1024;

/**
 * Hard upper bound on `rows.length`.
 *
 * ⚠️ ITS ROLE INVERTED IN ADM-35 — from backstop to PRIMARY BOUND, on the S3
 * path. Read the history before changing it, because the two paths want
 * opposite things from this number.
 *
 * **On the body path (the original, still live during the dual-path release)**
 * this is a loose backstop and the byte budget above is the real limit. The
 * docblock here used to state an invariant — *"it must stay loose enough that
 * bytes always decide first"* — and that was a correctness requirement, not a
 * preference: tRPC runs `.input()` parsing BEFORE the procedure body, so a row
 * bound tight enough to trip on a real file pre-empts the byte guard and
 * delivers the wrong error (a `BAD_REQUEST` carrying a stringified Zod issue
 * array instead of `PAYLOAD_TOO_LARGE` with prose naming the limit and the
 * remedy). At 50,000 that was a live bug: a single-column email export of
 * 60,000 rows serializes to ~660 KB — UNDER the byte budget — yet was refused
 * on rows, with a message about rows. An email-only list is one of the most
 * common nonprofit imports there is.
 *
 * **On the S3 path that invariant is VOID**, because there is no request body
 * left for bytes to decide about: the rows arrive as an object, and the only
 * thing bounding how many of them the Lambda will pull into memory is this
 * number. It stops being "loose enough not to interfere" and becomes the
 * primary bound — the thing standing between a 2-million-row upload and an
 * out-of-memory Lambda.
 *
 * **150,000 IS MEASURED, NOT CHOSEN** (ADM-35). The previous 250,000 was
 * inherited from the body-path era, where it was derived as "more rows than the
 * byte budget could possibly hold" (~69k for an 810 KB budget) and explicitly
 * was NOT a memory bound. Once it became the primary bound it needed a real
 * basis, so the full validate path was measured end to end — parse, then
 * `validateJob`, then `serializeMappedDataset` — over a realistic 10-column
 * contacts export:
 *
 *   |    rows | source NDJSON | mapped.jsonl | wall clock | peak RSS |
 *   |---------|---------------|--------------|------------|----------|
 *   |  50,000 |         8 MB  |       18 MB  |     571 ms |  315 MB  |
 *   | 100,000 |        17 MB  |       36 MB  |   1,121 ms |  438 MB  |
 *   | 150,000 |        25 MB  |       54 MB  |   1,660 ms |  503 MB  |
 *
 * TWO FINDINGS, both of which changed the design:
 *
 * 1. **CPU is not the constraint.** The pre-build estimate put
 *    `serializeMappedDataset` at 4–12s and the total at 9–20s against the 30s
 *    ceiling. It is ~15x faster than that. Nothing here is close to timing out.
 * 2. **MEMORY is the constraint.** Peak RSS grows roughly linearly at ~3.3 MB
 *    per 1,000 rows, and the measurement above EXCLUDES the S3 client buffering
 *    the GET body and the PUT of a 54 MB `mapped.jsonl`. Extrapolated, 250,000
 *    rows lands near 840 MB against a 1024 MB Lambda — no useful headroom, and
 *    300,000 would OOM.
 *
 * 150,000 keeps roughly 2x memory headroom at a size measured to work. Raising
 * it is a MEMORY decision: re-measure RSS first, and raise the Lambda's memory
 * in `infra/imports.ts` in the same change, or the failure mode is an OOM kill
 * rather than a refusal anyone can read.
 *
 * Measured locally (arm64, Node 20). Lambda arm64 at 1024 MB is slower
 * per-core, so wall clock could be several times higher — still far inside 30s.
 * The RSS figure is the one that transfers most directly.
 *
 * ⚠️ **DO NOT LOWER THIS BELOW ~138,000 WHILE THE BODY ARM EXISTS.** The
 * body-path invariant still applies on its own arm: the narrowest possible row
 * (`["a"],` = 6 bytes) means an 810 KB body can hold ~138,240 rows, and if this
 * bound drops under that, some under-budget file gets refused on ROWS by Zod's
 * `.input()` parse before the byte guard runs — delivering a BAD_REQUEST
 * carrying a stringified issue array instead of PAYLOAD_TOO_LARGE with prose.
 * The margin is now 8%, not the comfortable 80% it was at 250,000. A test
 * derives that floor from the budget rather than restating this number, so it
 * will fail rather than let the mistake through — but the tight margin is worth
 * knowing before touching either constant. Release 2 deletes the body arm and
 * this consideration with it.
 */
export const VALIDATE_MAX_ROWS = 150_000;

/**
 * Byte budget for the metadata-only `validate` body on the S3 path.
 *
 * ONCE THE ROWS LEAVE THE BODY, `headers` IS THE ONLY USER-CONTROLLED FIELD
 * LEFT — and it is not small by construction. It is capped at 500 ENTRIES but
 * each entry's LENGTH is unbounded, so 500 headers of 2,000 characters
 * serialize to roughly 1 MB on their own. That is over the edge ceiling with no
 * rows in the payload at all, and it is reachable by accident rather than by
 * attack: a spreadsheet with a merged header row, or pasted prose sitting in
 * row 1, produces exactly this shape.
 *
 * 64 KB is far above any legitimate header row (500 columns averaging 128
 * characters) and far below the 1 MiB wall, so the refusal is always ours and
 * never CloudFront's. Unlike `VALIDATE_MAX_BODY_BYTES` this is not derived from
 * a probe, because it does not need to be: it is not trying to sit just under
 * the edge limit, it is trying to reject an absurd header list early.
 */
export const VALIDATE_MAX_HEADERS_BYTES = 64 * 1024;

/**
 * Serialized byte size of the user-controlled request payload, measured the way
 * the transport measures it.
 *
 * MEASURES `headers` AS WELL AS `rows`, and that is not a detail. `headers` is
 * bounded at 500 entries but each entry's LENGTH is unbounded, so 500 headers
 * of 2,000 characters serialize to ~1MB on their own — over the edge ceiling
 * with `rows: [['a']]`. Measuring rows alone left that path wide open, and it
 * is reachable by accident, not just by attack: a spreadsheet with a merged
 * header row or pasted prose in row 1 produces exactly this shape.
 *
 * Uses `JSON.stringify` because that is literally what travels; estimating from
 * cell lengths undercounts escaping, quoting, and the array punctuation, and
 * undercounting here reintroduces the edge 413.
 *
 * Runs on both sides of the wire: the wizard calls it to refuse before sending,
 * the procedure calls it to refuse what it received.
 */
export function measureValidateBodyBytes(
  rows: ReadonlyArray<ReadonlyArray<string | null>>,
  headers: ReadonlyArray<string> = [],
): number {
  // TextEncoder over the serialized form: `String.length` is UTF-16 code units
  // and undercounts every non-ASCII character, which nonprofit contact data
  // (accented names, non-Latin scripts) is full of.
  return new TextEncoder().encode(JSON.stringify({ headers, rows })).length;
}

/**
 * The user-facing refusal message. One string, used by the client pre-flight
 * and the server bound alike, so a nonprofit meets the SAME explanation
 * wherever the limit is enforced.
 *
 * Names the remedy, not just the limit — "too big" without "split it" is a
 * dead end for a non-technical user.
 */
/**
 * Serialized byte size of `headers` alone — the S3 path's equivalent of
 * `measureValidateBodyBytes`.
 *
 * Measures the same way, for the same reason: `JSON.stringify` because that is
 * literally what travels, and `TextEncoder` because `String.length` counts
 * UTF-16 code units and undercounts every accented or non-Latin character.
 */
export function measureValidateHeaderBytes(headers: ReadonlyArray<string>): number {
  return new TextEncoder().encode(JSON.stringify({ headers })).length;
}

/**
 * The refusal for an oversized header row.
 *
 * DELIBERATELY NOT `validateBodyTooLargeMessage`. That message's remedy is
 * "split it into files of about N rows", which is actively misleading here —
 * splitting a file by rows does nothing about a header row that is too large,
 * so the user would do the work and meet the identical refusal. The failure is
 * almost always a malformed header line rather than a genuinely huge one (a
 * merged cell, or prose pasted into row 1), so the remedy names that instead.
 */
export function validateHeadersTooLargeMessage(
  actualBytes: number,
  columnCount: number,
): string {
  const actualKB = (actualBytes / 1024).toFixed(1);
  const limitKB = (VALIDATE_MAX_HEADERS_BYTES / 1024).toFixed(0);
  return (
    `This file's header row is too large to process (${actualKB} KB across ` +
    `${columnCount.toLocaleString('en-US')} columns; the limit is ${limitKB} KB). ` +
    `This usually means the first row of the file holds something other than ` +
    `column names — merged cells, or a title or note pasted above the data. ` +
    `Check that row 1 contains only column headers, then upload the file again.`
  );
}

export function validateBodyTooLargeMessage(actualBytes: number, rowCount: number): string {
  const actualMB = (actualBytes / (1024 * 1024)).toFixed(2);
  const limitMB = (VALIDATE_MAX_BODY_BYTES / (1024 * 1024)).toFixed(2);
  // `Math.max(1, x)` alone does NOT sanitize this: `Math.max(1, NaN)` is NaN,
  // not 1. `actualBytes: 0` makes the ratio Infinity and `0 * Infinity` NaN,
  // which would render "about NaN rows" — worse than saying nothing. Reachable
  // whenever the payload is over budget on `headers` with zero data rows, so
  // guard the division explicitly rather than trusting the clamp.
  const ratio = actualBytes > 0 ? rowCount * (VALIDATE_MAX_BODY_BYTES / actualBytes) : 0;
  const suggestedRows = Number.isFinite(ratio) ? Math.max(1, Math.floor(ratio)) : 1;
  return (
    `This file is too large to validate in one request (${actualMB} MB of row ` +
    `data; the limit is ${limitMB} MB). Split it into files of about ` +
    `${suggestedRows.toLocaleString('en-US')} rows or fewer and import them one ` +
    `at a time.`
  );
}
